const REVEAL_SELECTOR = "[data-reveal]";

export function setupScrollEffects() {
  let root = document.documentElement;
  let reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  setupNavigationEffects(reducedMotion);

  if (reducedMotion || !("IntersectionObserver" in window)) {
    root.classList.add("motion-reduced");
    return;
  }

  root.classList.add("motion-ready");

  let revealElements = Array.from(
    document.querySelectorAll<HTMLElement>(REVEAL_SELECTOR),
  );
  let groups = new Map<Element | null, number>();

  for (let element of revealElements) {
    let group = element.parentElement;
    let order = groups.get(group) ?? 0;
    groups.set(group, order + 1);
    element.style.setProperty("--reveal-delay", `${Math.min(order, 7) * 65}ms`);
  }

  let observer = new IntersectionObserver(
    (entries) => {
      for (let entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add("is-visible");
        observer.unobserve(entry.target);
      }
    },
    { threshold: 0.12, rootMargin: "0px 0px -7% 0px" },
  );

  for (let element of revealElements) observer.observe(element);

  let parallaxElements = Array.from(
    document.querySelectorAll<HTMLElement>("[data-parallax]"),
  );
  let frame = 0;

  function updateParallax() {
    frame = 0;
    let offset = Math.min(window.scrollY * 0.045, 26);
    for (let element of parallaxElements) {
      element.style.setProperty("--parallax-y", `${offset}px`);
    }
  }

  function onScroll() {
    if (frame) return;
    frame = requestAnimationFrame(updateParallax);
  }

  window.addEventListener("scroll", onScroll, { passive: true });
  updateParallax();

  window.addEventListener(
    "pagehide",
    () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    },
    { once: true },
  );
}

function setupNavigationEffects(reducedMotion: boolean) {
  let header = document.querySelector<HTMLElement>("[data-site-header]");
  if (!header) return;

  let navShell = header.querySelector<HTMLElement>("[data-nav-shell]");
  let links = Array.from(
    header.querySelectorAll<HTMLAnchorElement>("[data-nav-link]"),
  );
  let desktopNav = header.querySelector<HTMLElement>("[data-nav-list]");
  let sectionMap = new Map<string, HTMLElement>();
  for (let link of links) {
    let id = link.hash.slice(1);
    let section = id ? document.getElementById(id) : null;
    if (section) sectionMap.set(id, section);
  }
  let sections = Array.from(sectionMap.values());
  let previousY = window.scrollY;
  let ticking = false;

  function updateNavigation() {
    ticking = false;
    let scrollY = Math.max(window.scrollY, 0);
    let scrollingDown = scrollY > previousY + 2;
    let scrollingUp = scrollY < previousY - 2;
    let maxScroll = Math.max(
      document.documentElement.scrollHeight - window.innerHeight,
      1,
    );

    header?.classList.toggle("is-floating", scrollY > 48);
    if (!reducedMotion && scrollY > 360 && scrollingDown) {
      header?.classList.add("is-compact");
    } else if (scrollingUp || scrollY <= 360 || reducedMotion) {
      header?.classList.remove("is-compact");
    }
    let sectionRects = sections.map((section) => section.getBoundingClientRect());
    let activeIndex = getActiveSectionIndex(
      sectionRects,
      window.innerHeight,
      maxScroll - scrollY,
    );
    let active = sections[activeIndex];
    let milestoneProgress = sections.length > 1
      ? activeIndex / (sections.length - 1)
      : 0;
    header?.style.setProperty("--nav-progress", String(milestoneProgress));
    for (let link of links) {
      let activeHash = link.closest("[data-mobile-nav]")
        ? getMobileNavigationHash(active?.id)
        : `#${active?.id}`;
      let isActive = link.hash === activeHash;
      link.classList.toggle("active", isActive);
      if (isActive) link.setAttribute("aria-current", "page");
      else link.removeAttribute("aria-current");
    }
    let activeDesktopLink = desktopNav?.querySelector<HTMLAnchorElement>(
      `[data-nav-link][href="#${active?.id}"]`,
    );
    if (desktopNav && activeDesktopLink) {
      let indicatorX = activeDesktopLink.offsetLeft + activeDesktopLink.offsetWidth / 2 - 2.5;
      desktopNav.style.setProperty("--nav-indicator-x", `${indicatorX}px`);
      desktopNav.style.setProperty("--nav-indicator-visible", "1");
    }

    previousY = scrollY;
  }

  function requestUpdate() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(updateNavigation);
  }

  function updateGlassHighlight(event: PointerEvent) {
    if (!navShell || reducedMotion) return;
    let bounds = navShell.getBoundingClientRect();
    let x = ((event.clientX - bounds.left) / bounds.width) * 100;
    let y = ((event.clientY - bounds.top) / bounds.height) * 100;
    navShell.style.setProperty("--glass-x", `${Math.min(Math.max(x, 0), 100)}%`);
    navShell.style.setProperty("--glass-y", `${Math.min(Math.max(y, 0), 100)}%`);
  }

  function resetGlassHighlight() {
    navShell?.style.removeProperty("--glass-x");
    navShell?.style.removeProperty("--glass-y");
  }

  window.addEventListener("scroll", requestUpdate, { passive: true });
  window.addEventListener("resize", requestUpdate);
  header.addEventListener("transitionend", requestUpdate);
  navShell?.addEventListener("pointermove", updateGlassHighlight, { passive: true });
  navShell?.addEventListener("pointerleave", resetGlassHighlight);
  updateNavigation();

  window.addEventListener(
    "pagehide",
    () => {
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
      header?.removeEventListener("transitionend", requestUpdate);
      navShell?.removeEventListener("pointermove", updateGlassHighlight);
      navShell?.removeEventListener("pointerleave", resetGlassHighlight);
    },
    { once: true },
  );
}

export function getMobileNavigationHash(sectionId?: string) {
  if (sectionId === "projects") return "#projects";
  if (["skills", "education", "achievements"].includes(sectionId ?? "")) {
    return "#skills";
  }
  if (sectionId === "contact") return "#contact";
  return "#home";
}

type SectionRect = Pick<DOMRect, "top" | "bottom" | "height">;

export function getActiveSectionIndex(
  sections: SectionRect[],
  viewportHeight: number,
  remainingScroll: number,
) {
  if (sections.length === 0) return 0;

  let activationLine = Math.min(viewportHeight * 0.34, 280);
  let activeIndex = 0;
  for (let index = 0; index < sections.length; index++) {
    if (sections[index].top <= activationLine) activeIndex = index;
  }

  let lastIndex = sections.length - 1;
  let lastSection = sections[lastIndex];
  let visibleFooterHeight = Math.max(
    0,
    Math.min(viewportHeight, lastSection.bottom) - Math.max(0, lastSection.top),
  );
  let footerActivationHeight = Math.min(lastSection.height * 0.25, 120);
  if (remainingScroll <= 3 || visibleFooterHeight >= footerActivationHeight) {
    activeIndex = lastIndex;
  }

  return activeIndex;
}
